import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { defaultInstance } from "../api/httpClient";
import { toaster } from "baseui/toast";
import { fetchAllPages } from "../utils/fetchAllPages";

// Types
export interface Author {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  /** Not returned by GET /author; kept so older callers and fixtures still type-check. */
  name?: string;
}

/** GET /author returns first_name + last_name (SAM-1098); there is no `name` column. */
export const authorDisplayName = (a: Pick<Author, 'first_name' | 'last_name' | 'name'>): string =>
  [a.first_name, a.last_name].filter((x) => x && x.trim()).join(' ').trim() || a.name?.trim() || '';

export interface ArticleType {
  id: number;
  name: string;
}

export interface Article {
  id?: number;
  status: 'draft' | 'published';
  title_es: string;
  title_en: string;
  content_es: string;
  content_en: string;
  author_id: number;
  art_img_url?: string;
  article_type_id: number;
  publish_date: string;
  expiration_date: string;
  creation_date?: string;
  user_types_ids: number[];
  action_link_object?: { id?: string | number; action?: string } | null;
}

interface ListResponse<T> {
  data: T[];
}

// Hook to fetch articles
export const useArticles = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['articles'],
    queryFn: () => fetchAllPages<Article>('/article'),
  });

  return {
    articles: (data as Article[] | undefined) || [],
    isLoading,
    error,
  };
};

// Hook to fetch authors
export const useAuthors = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['authors'],
    queryFn: async () => {
      const response = await defaultInstance<ListResponse<Author>>({
        url: '/author',
        method: 'GET',
      });
      return (response as unknown as ListResponse<Author>).data || [];
    },
  });

  return {
    authors: (data as Author[] | undefined) || [],
    isLoading,
    error,
  };
};

// Hook to fetch article types
export const useArticleTypes = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['articleTypes'],
    queryFn: async () => {
      const response = await defaultInstance<ListResponse<ArticleType>>({
        url: '/article_type',
        method: 'GET',
      });
      return (response as unknown as ListResponse<ArticleType>).data || [];
    },
  });

  return {
    articleTypes: (data as ArticleType[] | undefined) || [],
    isLoading,
    error,
  };
};

// Mutation hooks
export const useCreateArticle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Article>) => {
      return await defaultInstance({
        url: '/article',
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      toaster.positive('Article created successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to create article', { autoHideDuration: 3000 });
    },
  });
};

export const useUpdateArticle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Article> }) => {
      return await defaultInstance({
        url: `/article/${id}`,
        method: 'PUT',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      toaster.positive('Article updated successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to update article', { autoHideDuration: 3000 });
    },
  });
};

export const useDeleteArticle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      return await defaultInstance({
        url: `/article/${id}`,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      toaster.positive('Article deleted successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to delete article', { autoHideDuration: 3000 });
    },
  });
};

/** SAM-1095: Publish / Move to draft for several articles at once: one request each, one toast, one refetch. */
export const useSetArticlesStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, status }: { ids: number[]; status: 'draft' | 'published' }) => {
      const results = await Promise.allSettled(
        ids.map((id) => defaultInstance({ url: `/article/${id}`, method: 'PATCH', data: { status } })),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed) throw new Error(`${failed} of ${ids.length} articles could not be updated`);
      return ids.length;
    },
    onSuccess: (n, { status }) => {
      toaster.positive(`${n} article${n === 1 ? '' : 's'} ${status === 'published' ? 'published' : 'moved to draft'}`, { autoHideDuration: 3000 });
    },
    onError: (error: Error) => {
      toaster.negative(error?.message || 'Failed to update articles', { autoHideDuration: 3000 });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['articles'] }),
  });
};
